/**
 * Agents Overview Page — ENG-277 redesign.
 *
 * Hero cards for Live / Staging / Latest Draft,
 * collapsible version-history timeline below.
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, type Variants } from 'framer-motion';
import { PageHeader } from '@/components/layout/PageHeader';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Settings,
  Loader2,
  Plus,
  FlaskConical,
  ExternalLink,
  Trash2,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  GitCompareArrows,
  AppWindow,
  Maximize,
} from 'lucide-react';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/use-toast';
import salesAgentService from '@/lib/api/salesAgent';
import customDomainsService from '@/lib/api/customDomains';
import { getEmbeddedDemoUrl, getLiveDemoUrl, getStagingDemoUrl, getEmbeddedStagingDemoUrl } from '@/lib/demoUrls';
import promptVersionsService from '@/lib/api/promptVersions';
import type { PromptVersion, TransitionRequest, VersionsListResponse } from '@/lib/api/promptVersions';

/* ── Helpers ──────────────────────────────────────────────────────────── */

function statusBadge(status: string) {
  switch (status) {
    case 'live':
      return <Badge className="bg-success/15 text-success border-success/20">Live</Badge>;
    case 'staging':
      return <Badge className="bg-primary/10 text-foreground border-primary/15">Staging</Badge>;
    default:
      return <Badge variant="secondary">Draft</Badge>;
  }
}

function statusDotColor(status: string) {
  switch (status) {
    case 'live':
      return 'bg-success';
    case 'staging':
      return 'bg-foreground';
    default:
      return 'bg-muted-foreground/40';
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) +
    ', ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function getCreatorInitials(version: PromptVersion): string {
  const source = version.created_by?.trim();
  if (!source) return 'SY';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (source.includes('@')) {
    return source.split('@')[0].slice(0, 2).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function renderCreator(version: PromptVersion) {
  if (!version.created_by) {
    return (
      <span className="text-caption text-muted-foreground/70">
        Created by System
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar className="h-6 w-6 border border-border/70">
        <AvatarImage src={version.created_by_avatar_url || undefined} alt={version.created_by} />
        <AvatarFallback className="text-[10px] font-medium">
          {getCreatorInitials(version)}
        </AvatarFallback>
      </Avatar>
      <span className="text-caption text-muted-foreground truncate">
        {version.created_by}
      </span>
    </div>
  );
}

/* ── Motion ───────────────────────────────────────────────────────────── */

const heroContainerVariants: Variants = {
  animate: { transition: { staggerChildren: 0.08 } },
};

const heroCardVariants: Variants = {
  initial: { opacity: 0, y: 12, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ── Page ─────────────────────────────────────────────────────────────── */

const AgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization, isAdmin } = useOrganization();
  const orgId = currentOrganization?.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  /* ── Queries ── */

  const { data: salesAgent } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.getSalesAgent(orgId);
      if (response.success && response.data) return response.data;
      return null;
    },
    enabled: !!orgId && isAdmin,
    retry: false,
  });

  const { data: customDomainState } = useQuery({
    queryKey: ['custom-domain', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await customDomainsService.getCustomDomain(orgId);
      if (response.success && response.data) return response.data;
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  const liveDemoUrl = getLiveDemoUrl(salesAgent?.demo_slug, customDomainState?.custom_domain);
  const embeddedDemoUrl = getEmbeddedDemoUrl(salesAgent?.demo_slug);
  const stagingDemoUrl = getStagingDemoUrl(salesAgent?.staging_slug);
  const embeddedStagingDemoUrl = getEmbeddedStagingDemoUrl(salesAgent?.staging_slug);

  const { data: versionsData, isLoading: isVersionsLoading } = useQuery({
    queryKey: ['prompt-versions', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await promptVersionsService.listVersions(orgId);
      if (res.success && res.data) return res.data;
      throw new Error(res.error || 'Failed to load versions');
    },
    enabled: !!orgId,
  });

  const versions = versionsData?.versions ?? [];
  const liveVersion = versions.find((v) => v.status === 'live');
  const stagingVersion = versions.find((v) => v.status === 'staging');
  const latestDraft = versions.find((v) => v.status === 'draft');

  /* ── Mutations ── */

  const transitionMutation = useMutation({
    mutationFn: async (body: TransitionRequest) => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await promptVersionsService.transitionVersion(orgId, body);
      if (res.success && res.data) return res.data;
      throw new Error(res.error || 'Transition failed');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['prompt-versions', orgId] });
      queryClient.invalidateQueries({ queryKey: ['sales-agent', orgId] });
      toast({ title: 'Success', description: data.message });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const [versionToDelete, setVersionToDelete] = useState<number | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (version: number) => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await promptVersionsService.deleteVersion(orgId, version);
      if (res.success && res.data) return res.data;
      throw new Error(res.error || 'Failed to delete version');
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['prompt-versions', orgId], (old: VersionsListResponse | undefined) =>
        old ? { ...old, versions: old.versions.filter((v) => v.version !== data.version) } : old,
      );
      queryClient.invalidateQueries({ queryKey: ['prompt-versions', orgId] });
      toast({ title: 'Success', description: data.message });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  /* ── Navigation ── */

  const handleVersionClick = (v: PromptVersion) => {
    if (!liveVersion) {
      navigate(`/agents/configure?version=${v.version}`);
      return;
    }
    // For the live version itself, compare against the previous version
    if (liveVersion.version === v.version) {
      const previousVersion = versions.find((ver) => ver.version < v.version);
      if (previousVersion) {
        navigate(`/agents/configure?base=${previousVersion.version}&compare=${v.version}&selected=${v.version}`);
      } else {
        navigate(`/agents/configure?version=${v.version}`);
      }
    } else {
      const base = Math.min(liveVersion.version, v.version);
      const compare = Math.max(liveVersion.version, v.version);
      navigate(`/agents/configure?base=${base}&compare=${compare}&selected=${v.version}`);
    }
  };

  /* ── Collapsible state ── */
  const [historyOpen, setHistoryOpen] = useState(false);

  /* ── Preview dropdown (reused in hero cards) ── */

  const renderPreviewDropdown = (role: 'live' | 'staging') => {
    const fullPageUrl = role === 'live' ? liveDemoUrl : stagingDemoUrl;
    const embeddedUrl = role === 'live' ? embeddedDemoUrl : embeddedStagingDemoUrl;
    const disabled = !fullPageUrl && !embeddedUrl;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Preview
            <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            disabled={!fullPageUrl}
            onClick={() => fullPageUrl && window.open(fullPageUrl, '_blank')}
          >
            <AppWindow className="h-4 w-4 mr-2" />
            Full Page
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!embeddedUrl}
            onClick={() => embeddedUrl && window.open(embeddedUrl, '_blank')}
          >
            <Maximize className="h-4 w-4 mr-2" />
            Embedded
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  /* ── Render helpers ── */

  const renderHeroCard = (
    role: 'live' | 'staging' | 'draft',
    version: PromptVersion | undefined,
  ) => {
    if (!version) {
      return (
        <motion.div variants={heroCardVariants} className="h-full">
          <div className="border border-dashed border-border rounded-lg bg-card p-4 shadow-card h-full flex flex-col">
            <p className="text-sm text-muted-foreground">
              {role === 'live' && 'No live version yet'}
              {role === 'staging' && 'No version in staging'}
              {role === 'draft' && 'No draft versions'}
            </p>
            {role === 'draft' && (
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => {
                  const lv = versions.find((ver) => ver.status === 'live');
                  navigate(lv ? `/agents/create-version?version=${lv.version}` : '/agents/create-version');
                }}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Coach New Version
              </Button>
            )}
          </div>
        </motion.div>
      );
    }

    const heroBadge = role === 'draft'
      ? <Badge variant="secondary">Latest Draft</Badge>
      : statusBadge(version.status);

    return (
      <motion.div variants={heroCardVariants} className="h-full">
        <div className="border border-border rounded-lg bg-card p-4 shadow-card transition-shadow hover:shadow-sm h-full flex flex-col">
          {/* Version + date */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-section-title text-foreground font-semibold">
              Version {version.version}
            </span>
            <span className="text-caption text-muted-foreground/60 whitespace-nowrap">
              {formatDate(version.created_at)}
            </span>
          </div>

          {/* Badge */}
          <div className="mb-3">
            {heroBadge}
          </div>

          <div className="space-y-3 mb-4">
            <p className="text-sm leading-relaxed text-foreground">
              {version.summary || 'No summary available for this version yet.'}
            </p>
            {renderCreator(version)}
          </div>

          {/* Actions — pushed to bottom */}
          <div className="flex items-center gap-1.5 flex-wrap mt-auto">
            {/* Primary transition action */}
            {role === 'staging' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    onClick={() => transitionMutation.mutate({ version: version.version, action: 'publish_to_live' })}
                    disabled={transitionMutation.isPending}
                  >
                    {transitionMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    Publish Live
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Promote this version to live</TooltipContent>
              </Tooltip>
            )}
            {role === 'draft' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    onClick={() => transitionMutation.mutate({ version: version.version, action: 'deploy_to_staging' })}
                    disabled={transitionMutation.isPending}
                  >
                    {transitionMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    Deploy to Staging
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Deploy this draft to staging for testing</TooltipContent>
              </Tooltip>
            )}

            {/* Preview dropdown (live + staging only) */}
            {(role === 'live' || role === 'staging') && renderPreviewDropdown(role)}

            {/* Compare / View */}
            {/* Compare — always available */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleVersionClick(version)}
                >
                  <GitCompareArrows className="h-3.5 w-3.5 mr-1.5" />
                  Compare
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {role === 'live'
                  ? 'Compare with previous version'
                  : 'Compare changes with live version'}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderTimelineRow = (v: PromptVersion, idx: number, arr: PromptVersion[]) => {
    const isFirst = idx === 0;
    const isLast = idx === arr.length - 1;

    return (
      <div key={v.version} className="group relative flex min-h-[88px] items-stretch py-1.5">
        {/* Timeline spine — continuous vertical line with dot */}
        <div className="absolute left-[13px] top-0 bottom-0 flex flex-col items-center">
          <div className={`w-px flex-1 ${isFirst ? 'bg-transparent' : 'bg-border'}`} />
          <div className={`w-2 h-2 rounded-full shrink-0 ${statusDotColor(v.status)}`} />
          <div className={`w-px flex-1 ${isLast ? 'bg-transparent' : 'bg-border'}`} />
        </div>

        {/* Row content — fixed columns for alignment */}
        <div className="flex flex-1 items-start gap-3 pl-9 pr-2 min-w-0">
          <div className="min-w-0 flex-1 space-y-2 py-1.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="shrink-0 font-medium text-sm text-left hover:underline cursor-pointer flex items-center gap-1"
                    onClick={() => handleVersionClick(v)}
                  >
                    v{v.version}
                    <GitCompareArrows className="h-3 w-3 ml-1 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {liveVersion && v.version !== liveVersion.version
                    ? 'Click to compare changes with live version'
                    : 'Click to view version configuration'}
                </TooltipContent>
              </Tooltip>

              <div className="shrink-0">
                {statusBadge(v.status)}
              </div>

              <span className="text-caption text-muted-foreground">
                {formatDate(v.created_at)}
              </span>
            </div>

            <p className="text-sm text-foreground leading-relaxed">
              {v.summary || 'No summary available for this version yet.'}
            </p>

            {renderCreator(v)}
          </div>

          <div className="shrink-0 pt-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleVersionClick(v)}>
                  <GitCompareArrows className="h-4 w-4 mr-2" />
                  View Changes
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/agents/configure?version=${v.version}`)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                {v.status === 'draft' && (
                  <DropdownMenuItem
                    onClick={() => transitionMutation.mutate({ version: v.version, action: 'deploy_to_staging' })}
                    disabled={transitionMutation.isPending}
                  >
                    Deploy to Staging
                  </DropdownMenuItem>
                )}
                {v.status === 'staging' && (
                  <DropdownMenuItem
                    onClick={() => transitionMutation.mutate({ version: v.version, action: 'publish_to_live' })}
                    disabled={transitionMutation.isPending}
                  >
                    Publish Live
                  </DropdownMenuItem>
                )}
                {(v.status === 'live' || v.status === 'staging') && (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        const url = v.status === 'live' ? liveDemoUrl : stagingDemoUrl;
                        if (url) window.open(url, '_blank');
                      }}
                    >
                      <AppWindow className="h-4 w-4 mr-2" />
                      Preview Full Page
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const url = v.status === 'live' ? embeddedDemoUrl : embeddedStagingDemoUrl;
                        if (url) window.open(url, '_blank');
                      }}
                    >
                      <Maximize className="h-4 w-4 mr-2" />
                      Preview Embedded
                    </DropdownMenuItem>
                  </>
                )}
                {v.status === 'draft' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setVersionToDelete(v.version)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  };

  /* ── Main render ────────────────────────────────────────────────────── */

  return (
    <>
      <Helmet>
        <title>Agent Versions | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader title="Agent Versions" />

        {/* Content */}
        <TooltipProvider delayDuration={200}>
          <div className="flex-1 px-4 sm:px-6 md:px-8 py-6 space-y-8">
            {/* Current Versions — Hero Cards */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-medium text-muted-foreground">
                  Current Versions
                  <HelpTooltip text="Each version is a snapshot of your agent's prompt and behavior." />
                </h2>
                {isAdmin && salesAgent && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/agents/configure')}
                    >
                      <Settings className="h-3.5 w-3.5 mr-1.5" />
                      Configure
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/training?tab=simulations')}
                    >
                      <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
                      Evaluate
                    </Button>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          onClick={() => {
                            const lv = versions.find((ver) => ver.status === 'live');
                            navigate(lv ? `/agents/create-version?version=${lv.version}` : '/agents/create-version');
                          }}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1.5" />
                          Coach New Version
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Coach a new version based on live</TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>

              {isVersionsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-sm text-muted-foreground">
                    {salesAgent
                      ? 'No versions yet. Coach your first version to get started.'
                      : 'Create your agent to get started.'}
                  </p>
                </div>
              ) : (
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch"
                  variants={heroContainerVariants}
                  initial="initial"
                  animate="animate"
                >
                  {renderHeroCard('live', liveVersion)}
                  {renderHeroCard('staging', stagingVersion)}
                  {renderHeroCard('draft', latestDraft)}
                </motion.div>
              )}
            </section>

            {/* Version History — Collapsible Timeline */}
            {versions.length > 0 && (
              <section>
                <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
                  <CollapsibleTrigger className="flex items-center gap-2 group/trigger cursor-pointer mb-3">
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]/trigger:rotate-90" />
                    <h2 className="text-sm font-medium text-muted-foreground">
                      Version History
                    </h2>
                    <span className="text-caption text-muted-foreground/60">
                      ({versions.length} version{versions.length !== 1 ? 's' : ''})
                    </span>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
                    <Card>
                      <CardContent className="py-2 px-3">
                        {versions.map((v, idx, arr) => renderTimelineRow(v, idx, arr))}
                      </CardContent>
                    </Card>
                  </CollapsibleContent>
                </Collapsible>
              </section>
            )}
          </div>
        </TooltipProvider>

        {/* Delete confirmation dialog */}
        <AlertDialog open={versionToDelete !== null} onOpenChange={(open) => { if (!open) setVersionToDelete(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete version v{versionToDelete}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this draft version. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (versionToDelete !== null) {
                    deleteMutation.mutate(versionToDelete);
                    setVersionToDelete(null);
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
};

export default AgentsPage;
